<template>
    <!-- MBR026-MBR028 Membership Tiers — member_app_UI/song_fa_members_membership_tiers -->
    <div class="page-header align-items-start mbr-tier">
        <span class="mask bg-white"></span>
        <div class="container p-0" v-if="isShowLayout">
            <header class="mbr-tier__bar">
                <button type="button" class="mbr-tier__back" :aria-label="$t('common.back')" @click="myTransactions()">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 class="mbr-tier__title">{{ $t('tier.title') }}</h1>
                <span class="mbr-tier__spacer" aria-hidden="true"></span>
            </header>

            <main class="mbr-tier__body" v-if="tiers.length">
                <section class="mbr-tier__browser">
                    <div class="mbr-tier__browser-head">
                        <span class="mbr-tier__eyebrow">{{ $t('tier.membership_tiers') }}</span>
                        <span class="mbr-tier__counter">{{ $t('tier.n_of_total', { n: selectedIndex + 1, total: tiers.length }) }}</span>
                    </div>

                    <article class="mbr-tier__card">
                        <span class="mbr-tier__ring mbr-tier__ring--lg" aria-hidden="true"></span>
                        <span class="mbr-tier__ring mbr-tier__ring--sm" aria-hidden="true"></span>

                        <div class="mbr-tier__card-top">
                            <div class="mbr-tier__card-text">
                                <span class="mbr-tier__chip" v-if="tierDefaultDisplay.is_current_tier">{{ $t('tier.current') }}</span>
                                <span class="mbr-tier__chip mbr-tier__chip--muted" v-else>{{ $t('tier.tier_n', { n: selectedIndex + 1 }) }}</span>
                                <h2 class="mbr-tier__name">{{ tierDefaultDisplay.tier_name }}</h2>
                                <p class="mbr-tier__kind">
                                    {{ tierDefaultDisplay.is_paid_member_ship ? $t('tier.paid_membership') : $t('tier.free_membership') }}
                                </p>
                            </div>
                            <div class="mbr-tier__badge">
                                <img :src="tierDefaultDisplay.tier_badge_url" alt="" />
                                <span v-if="tierDefaultDisplay.is_current_tier" class="mbr-tier__badge-check"
                                    aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                </span>
                            </div>
                        </div>

                        <div class="mbr-tier__card-foot"
                            v-if="tierDefaultDisplay.show_tier_condition && tierDefaultDisplay.note_text">
                            {{ tierDefaultDisplay.note_text }}
                        </div>
                    </article>

                    <div class="mbr-tier__nav">
                        <button type="button" class="mbr-tier__arrow" :aria-label="$t('tier.previous_tier')"
                            :disabled="selectedIndex === 0" @click="selectTier(selectedIndex - 1)">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <div class="mbr-tier__dots">
                            <button type="button" class="mbr-tier__dot" v-for="(tier, index) in tiers" :key="tier.id"
                                :class="index === selectedIndex ? 'mbr-tier__dot--on' : ''" :aria-label="tier.tier_name"
                                @click="selectTier(index)"></button>
                        </div>

                        <button type="button" class="mbr-tier__arrow" :aria-label="$t('tier.next_tier')"
                            :disabled="selectedIndex >= tiers.length - 1" @click="selectTier(selectedIndex + 1)">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    <button type="button" class="mbr-tier__peek" v-if="nextTier" @click="selectTier(selectedIndex + 1)">
                        <span class="mbr-tier__peek-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </span>
                        <span class="mbr-tier__peek-text">
                            <span class="mbr-tier__peek-name">{{ nextTier.tier_name }}</span>
                            <span class="mbr-tier__peek-sub">{{ $t('tier.next_available') }}</span>
                        </span>
                        <span class="mbr-tier__peek-chip">{{ nextTier.is_current_tier ? $t('tier.current') : $t('tier.locked') }}</span>
                    </button>
                </section>

                <section class="mbr-tier__panel"
                    v-if="tierDefaultDisplay.show_tier_description && tierDefaultDisplay.description">
                    <div class="mbr-tier__panel-head">
                        <h3 class="mbr-tier__panel-title">{{ $t('tier.description') }}</h3>
                    </div>
                    <p class="mbr-tier__text">{{ tierDefaultDisplay.description }}</p>
                </section>

                <section class="mbr-tier__panel" v-if="tierDefaultDisplay.show_tier_condition">
                    <div class="mbr-tier__panel-head">
                        <h3 class="mbr-tier__panel-title">{{ $t('tier.condition') }}</h3>
                        <span class="mbr-tier__panel-tag" v-if="tierDefaultDisplay.is_current_tier">{{ $t('tier.current_tier') }}</span>
                    </div>

                    <ul class="mbr-tier__rows">
                        <li class="mbr-tier__row">
                            {{ tierDefaultDisplay.is_paid_member_ship ? $t('tier.paid_membership') : $t('tier.free_membership') }}
                        </li>
                        <li class="mbr-tier__row"
                            v-if="tierDefaultDisplay.is_paid_member_ship && tierDefaultDisplay.already_achieved_a_paid_tier_previously">
                            {{ $t('tier.already_paid_tier') }}
                        </li>
                        <li class="mbr-tier__row mbr-tier__row--inline" v-if="tierDefaultDisplay.is_paid_member_ship">
                            <span>{{ $t('tier.membership_fee', { amount: tierDefaultDisplay.membership_fee_total_format }) }}</span>
                            <span class="mbr-tier__done" v-if="tierDefaultDisplay.completed_payment_membership_fee">
                                {{ $t('tier.completed') }}
                            </span>
                        </li>
                        <li class="mbr-tier__row"
                            v-if="tierDefaultDisplay.spending_achieve_tier > 0 && tierDefaultDisplay.spending_achieve_tier_format">
                            <template v-if="tierDefaultDisplay.upgrade_tier_by_admissions">
                                {{ $t('tier.visit_times', { count: tierDefaultDisplay.visit_achieve_tier_format }) }}
                            </template>
                            <template v-else>
                                {{ $t('tier.spend', { amount: tierDefaultDisplay.spending_achieve_tier_format }) }}
                            </template>
                        </li>
                        <li class="mbr-tier__row" v-if="tierDefaultDisplay.duration_achieve_membership">
                            {{ $t('tier.remain_loyalty', {
                                duration: tierDefaultDisplay.duration_achieve_membership + ' ' + (tierDefaultDisplay.duration_achieve_membership_unit_text
                                    ? tierDefaultDisplay.duration_achieve_membership_unit_text.label : '')
                            }) }}
                        </li>
                    </ul>
                </section>

                <section class="mbr-tier__panel" v-if="hasBenefit">
                    <div class="mbr-tier__panel-head">
                        <h3 class="mbr-tier__panel-title">{{ $t('tier.tier_benefits') }}</h3>
                        <span class="mbr-tier__panel-tag" v-if="tierDefaultDisplay.is_current_tier">{{ $t('tier.current_tier') }}</span>
                    </div>

                    <ul class="mbr-tier__benefits">
                        <li class="mbr-tier__benefit" v-if="tierDefaultDisplay.standard_rule_text">
                            <span class="mbr-tier__tick" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                            <span class="mbr-tier__benefit-text">
                                <span class="mbr-tier__benefit-title">{{ tierDefaultDisplay.standard_rule_text }}</span>
                            </span>
                        </li>

                        <li class="mbr-tier__benefit" v-if="tierDefaultDisplay.superior_rule_text">
                            <span class="mbr-tier__tick" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                            <span class="mbr-tier__benefit-text">
                                <span class="mbr-tier__benefit-title">{{ tierDefaultDisplay.superior_rule_text }}</span>
                            </span>
                        </li>

                        <li class="mbr-tier__benefit" v-if="tierDefaultDisplay.welcome_voucher_text">
                            <span class="mbr-tier__tick" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                            <span class="mbr-tier__benefit-text">
                                <span class="mbr-tier__benefit-title">{{ $t('tier.welcome_benefit') }}</span>
                                <span class="mbr-tier__benefit-line"
                                    v-for="(line, index) in tierDefaultDisplay.welcome_voucher_text.split('---')"
                                    :key="index">{{ line ? " - " + line : '' }}</span>
                            </span>
                        </li>

                        <li class="mbr-tier__benefit" v-if="tierDefaultDisplay.welcome_direct_cashback_text">
                            <span class="mbr-tier__tick" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                            <span class="mbr-tier__benefit-text">
                                <span class="mbr-tier__benefit-title">
                                    {{ tierDefaultDisplay.welcome_direct_cashback_text }}
                                </span>
                            </span>
                        </li>

                        <li class="mbr-tier__benefit" v-if="tierDefaultDisplay.birthday_voucher_text">
                            <span class="mbr-tier__tick" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                            <span class="mbr-tier__benefit-text">
                                <span class="mbr-tier__benefit-title">{{ $t('tier.birthday_benefit') }}</span>
                                <span class="mbr-tier__benefit-line"
                                    v-for="(line, index) in tierDefaultDisplay.birthday_voucher_text.split('---')"
                                    :key="index">{{ line ? " - " + line : '' }}</span>
                            </span>
                        </li>

                        <li class="mbr-tier__benefit" v-if="showStagingCashback && tierDefaultDisplay.birthday_direct_cashback_text">
                            <span class="mbr-tier__tick" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                            <span class="mbr-tier__benefit-text">
                                <span class="mbr-tier__benefit-title">
                                    {{ tierDefaultDisplay.birthday_direct_cashback_text }}
                                </span>
                            </span>
                        </li>

                        <li class="mbr-tier__benefit" v-if="showStagingCashback && tierDefaultDisplay.birthday_extra_rebase_text">
                            <span class="mbr-tier__tick" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                            <span class="mbr-tier__benefit-text">
                                <span class="mbr-tier__benefit-title">
                                    {{ tierDefaultDisplay.birthday_extra_rebase_text }}
                                </span>
                            </span>
                        </li>
                    </ul>
                </section>
            </main>

            <div class="mbr-tier__empty" v-else>
                <span class="mbr-tier__empty-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                        <path stroke-linecap="round" stroke-linejoin="round"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </span>
                <p class="mbr-tier__empty-title">{{ $t('tier.no_tier_yet') }}</p>
            </div>
        </div>

        <div class="position-fixed top-1 end-1 z-index-2">
            <material-snackbar v-if="snackbar === 'danger' || snackbar === 'success'" title="" date=""
                :description="message" :icon="{ component: 'campaign', color: 'white' }" :color="snackbar"
                :close-handler="closeSnackbar" />
        </div>
        <material-loading :active="loaderActive" />
    </div>

</template>

<script>
import { mapMutations, mapActions } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import TierService from "../services/tier.service";
import MaterialLoading from "@/components/MaterialLoading.vue";
import { errorMessage } from "@/utils/errorMessage";

export default {
    name: "sign-in",
    components: {
        MaterialSnackbar,
        MaterialLoading,
    },
    data() {
        return {
            transactionDetail: {},
            transactionType: [],
            snackbar: null,
            message: "",
            loaderActive: false,
            tiers: [],
            selectedIndex: 0,
            hasCurrentTier: false,
            isShowLayout: false,
            nodeEnv: process.env.VUE_APP_NODE_ENV,
        };
    },
    created() {
        this.getTiers()
    },
    // draws its own back header like the design, same chrome as VoucherBundleDetail
    beforeMount() {
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.add("mbr-sidenav-overlay");
        app.classList.remove("g-sidenav-pinned");
    },
    beforeUnmount() {
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.remove("mbr-sidenav-overlay");
        if (window.innerWidth > 1200) {
            app.classList.add("g-sidenav-pinned");
        }
    },
    computed: {
        // The tier the card + panels below are showing. Used to be a data field
        // written by tierDefault(); it now follows the carousel index.
        tierDefaultDisplay() {
            return this.tiers[this.selectedIndex] ?? {};
        },
        nextTier() {
            return this.tiers[this.selectedIndex + 1] ?? null;
        },
        // Direct cashback/rebate rows stay staging-only, and only alongside a cashback rule.
        showStagingCashback() {
            return this.nodeEnv === 'staging'
                && !!(this.tierDefaultDisplay.standard_rule_text || this.tierDefaultDisplay.superior_rule_text);
        },
        hasBenefit() {
            const tier = this.tierDefaultDisplay;
            return !!(tier.standard_rule_text || tier.superior_rule_text || tier.welcome_voucher_text
                || tier.welcome_direct_cashback_text || tier.birthday_voucher_text
                || (this.showStagingCashback && (tier.birthday_direct_cashback_text || tier.birthday_extra_rebase_text)));
        },
    },
    methods: {
        ...mapMutations(["toggleEveryDisplay", "toggleHideConfig", "toggleNavbarOnly"]),
        ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),
        getTiers() {
            this.isShowLayout = false;
            this.showLoader()
            TierService.getListTier().then(
                (response) => {
                    if (response.data.result.isSuccess) {
                        this.tiers = response.data.data
                        if (this.tiers.length) {
                            this.tiers.forEach((tier, index) => {
                                if (tier.is_current_tier) {
                                    this.hasCurrentTier = true
                                    this.selectTier(index)
                                }
                            });
                            if (!this.hasCurrentTier) {
                                this.selectTier(0)
                            }
                        }
                        this.isShowLayout = true
                    } else {
                        this.snackbar = 'danger'
                        this.message = errorMessage(response.data.result.message)
                        this.setTimeOutNotification()
                    }
                    this.hideLoader()
                },
                (error) => {
                    this.isShowLayout = false;
                    this.snackbar = 'danger'
                    this.message = errorMessage(error)
                    this.setTimeOutNotification()
                    this.hideLoader()
                }
            );
        },

        selectTier(index) {
            if (index < 0 || index > this.tiers.length - 1) {
                return
            }
            this.selectedIndex = index
        },

        myTransactions() {
            this.$router.go(-1)
        },

        setTimeOutNotification() {
            setTimeout(() => { this.snackbar = null; }, 3000);
        },

        closeSnackbar() {
            this.snackbar = null;
        },

        showLoader() {
            this.loaderActive = true;
        },
        hideLoader() {
            this.loaderActive = false;
        },
    },
};
</script>
<style scoped>
/* MBR026-MBR028 Membership Tiers.
   This block used to be unscoped: `.progressbar`, `.custom-icon-back` and `.btn-complted` leaked
   app-wide. The step rail is gone with the redesign; `.btn-complted` moved verbatim into
   assets/css/custom.css because BookingCompleted.vue renders it without defining it. */
.mbr-tier.page-header {
    min-height: 100vh;
    padding-bottom: 32px;
    font-family: var(--mbr-font-body);
}

/* Landing/Store ship an unscoped `* { font-family: Poppins }`; `*` hits every element directly so
   inheritance never reaches them. :where() keeps this at zero specificity. */
.mbr-tier :where(:not(.material-icons-round)) {
    font-family: var(--mbr-font-body);
}

.mbr-tier .mask {
    background: var(--mbr-color-canvas) !important;
    opacity: 1;
}

.mbr-tier .container {
    max-width: 440px;
    margin: 0 auto;
}

/* ---- top app bar ---- */
.mbr-tier__bar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 24px 20px 16px;
    background: var(--mbr-nav-bg);
    border-bottom: 1px solid rgba(var(--mbr-color-outline-rgb), 0.3);
    backdrop-filter: blur(var(--mbr-nav-blur));
    -webkit-backdrop-filter: blur(var(--mbr-nav-blur));
}

.mbr-tier__back,
.mbr-tier__spacer {
    flex: none;
    width: 40px;
    height: 40px;
}

.mbr-tier__back {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: var(--mbr-color-surface);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.4);
    border-radius: var(--mbr-radius-full);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
    color: var(--mbr-color-ink-headline);
    transition: transform 0.15s ease;
}

.mbr-tier__back:active {
    transform: scale(0.95);
}

.mbr-tier__back svg {
    width: 20px;
    height: 20px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-tier__title {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    font-family: var(--mbr-font-headline);
    font-size: 18px;
    font-weight: 800;
    line-height: 24px;
    letter-spacing: -0.01em;
    color: var(--mbr-color-ink-headline);
    text-align: center;
}

.mbr-tier__body {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 16px 20px 0;
}

/* ---- MBR027 tier browser ---- */
.mbr-tier__browser-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4px 12px;
}

.mbr-tier__eyebrow {
    font-size: var(--mbr-label-caps-size);
    font-weight: var(--mbr-label-caps-weight);
    line-height: var(--mbr-label-caps-line);
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(var(--mbr-color-ink-secondary-rgb), 0.7);
}

.mbr-tier__counter {
    font-size: var(--mbr-label-sm-size);
    font-weight: var(--mbr-label-sm-weight);
    line-height: var(--mbr-label-sm-line);
    color: var(--mbr-color-primary);
}

.mbr-tier__card {
    position: relative;
    overflow: hidden;
    padding: 24px;
    border: 1px solid rgba(var(--mbr-color-peach-rgb), 0.2);
    border-radius: var(--mbr-radius-xl);
    background: linear-gradient(135deg, var(--mbr-color-primary) 0%, var(--mbr-color-primary-dark) 100%);
    box-shadow: var(--mbr-shadow-card);
    color: var(--mbr-color-on-primary);
}

.mbr-tier__ring {
    position: absolute;
    border-radius: var(--mbr-radius-full);
    pointer-events: none;
}

.mbr-tier__ring--lg {
    right: -48px;
    bottom: -48px;
    width: 192px;
    height: 192px;
    border: 1px solid rgba(var(--mbr-color-peach-rgb), 0.1);
}

.mbr-tier__ring--sm {
    right: -16px;
    bottom: -16px;
    width: 128px;
    height: 128px;
    border: 1px solid rgba(var(--mbr-color-peach-rgb), 0.15);
}

.mbr-tier__card-top {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.mbr-tier__chip {
    display: inline-flex;
    align-items: center;
    margin-bottom: 8px;
    padding: 2px 10px;
    border-radius: var(--mbr-radius-full);
    background: var(--mbr-color-peach);
    font-family: var(--mbr-font-headline);
    font-size: 10px;
    font-weight: 800;
    line-height: 16px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--mbr-color-primary);
}

.mbr-tier__chip--muted {
    background: rgba(var(--mbr-color-peach-rgb), 0.18);
    color: var(--mbr-color-peach);
}

.mbr-tier__name {
    margin: 0;
    font-family: var(--mbr-font-headline);
    font-size: 20px;
    font-weight: 800;
    line-height: 26px;
    letter-spacing: -0.015em;
    color: var(--mbr-color-on-primary);
}

.mbr-tier__kind {
    margin: 2px 0 0;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0.04em;
    color: rgba(var(--mbr-color-surface-rgb), 0.7);
}

.mbr-tier__badge {
    position: relative;
    flex: none;
    width: 56px;
    height: 56px;
}

.mbr-tier__badge img {
    width: 56px;
    height: 56px;
    border-radius: var(--mbr-radius-full);
    border: 1px solid rgba(var(--mbr-color-peach-rgb), 0.4);
    background: rgba(var(--mbr-color-peach-rgb), 0.2);
    object-fit: cover;
}

.mbr-tier__badge-check {
    position: absolute;
    right: -2px;
    bottom: -2px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: 2px solid var(--mbr-color-primary-dark);
    border-radius: var(--mbr-radius-full);
    background: var(--mbr-color-positive);
    color: var(--mbr-color-on-primary);
}

.mbr-tier__badge-check svg {
    width: 10px;
    height: 10px;
}

.mbr-tier__card-foot {
    position: relative;
    z-index: 1;
    margin-top: 32px;
    padding-top: 16px;
    border-top: 1px solid rgba(var(--mbr-color-surface-rgb), 0.1);
    font-size: 12px;
    line-height: 18px;
    color: rgba(var(--mbr-color-surface-rgb), 0.8);
}

/* ---- carousel controls ---- */
.mbr-tier__nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    padding: 0 8px;
}

.mbr-tier__arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: var(--mbr-color-surface);
    border: 1px solid var(--mbr-color-outline);
    border-radius: var(--mbr-radius-full);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
    color: var(--mbr-color-primary);
    transition: transform 0.15s ease;
}

.mbr-tier__arrow svg {
    width: 16px;
    height: 16px;
}

.mbr-tier__arrow:active:not(:disabled) {
    transform: scale(0.95);
}

.mbr-tier__arrow:disabled {
    border-color: rgba(var(--mbr-color-outline-rgb), 0.4);
    color: rgba(var(--mbr-color-ink-secondary-rgb), 0.4);
    opacity: 0.4;
    cursor: not-allowed;
}

.mbr-tier__dots {
    display: flex;
    align-items: center;
    gap: 6px;
}

.mbr-tier__dot {
    width: 8px;
    height: 8px;
    padding: 0;
    border: 0;
    border-radius: var(--mbr-radius-full);
    background: var(--mbr-color-outline);
    transition: width 0.15s ease, background 0.15s ease;
}

.mbr-tier__dot--on {
    width: 24px;
    background: var(--mbr-color-primary);
}

/* ---- next-tier peek ---- */
.mbr-tier__peek {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    margin-top: 12px;
    padding: 16px;
    text-align: left;
    background: rgba(var(--mbr-color-tint-dark-rgb), 0.8);
    border: 1px solid var(--mbr-color-outline);
    border-radius: var(--mbr-radius-lg);
    color: var(--mbr-color-ink-secondary);
}

.mbr-tier__peek-icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: var(--mbr-radius-full);
    background: var(--mbr-color-surface);
    color: var(--mbr-color-ink-secondary);
}

.mbr-tier__peek-icon svg {
    width: 16px;
    height: 16px;
}

.mbr-tier__peek-text {
    flex: 1 1 auto;
    min-width: 0;
}

.mbr-tier__peek-name {
    display: block;
    font-family: var(--mbr-font-headline);
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    color: var(--mbr-color-ink-headline);
}

.mbr-tier__peek-sub {
    display: block;
    font-size: 11px;
    line-height: 16px;
    color: rgba(var(--mbr-color-ink-secondary-rgb), 0.7);
}

.mbr-tier__peek-chip {
    flex: none;
    padding: 4px 10px;
    border-radius: var(--mbr-radius-full);
    background: var(--mbr-color-peach);
    font-size: 11px;
    font-weight: 700;
    line-height: 14px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--mbr-color-primary);
}

/* ---- MBR028 detail panels ---- */
.mbr-tier__panel {
    padding: 20px;
    background: var(--mbr-color-card);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.4);
    border-radius: var(--mbr-radius-lg);
    box-shadow: var(--mbr-shadow-card);
}

.mbr-tier__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(var(--mbr-color-outline-rgb), 0.3);
}

.mbr-tier__panel-title {
    margin: 0;
    font-family: var(--mbr-font-headline);
    font-size: 15px;
    font-weight: 800;
    line-height: 22px;
    letter-spacing: -0.01em;
    color: var(--mbr-color-ink-headline);
}

.mbr-tier__panel-tag {
    flex: none;
    font-size: var(--mbr-label-caps-size);
    font-weight: var(--mbr-label-caps-weight);
    line-height: var(--mbr-label-caps-line);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--mbr-color-primary);
}

.mbr-tier__text {
    margin: 16px 0 0;
    font-size: var(--mbr-body-sm-size);
    line-height: 20px;
    color: var(--mbr-color-ink-secondary);
}

.mbr-tier__rows {
    margin: 0;
    padding: 16px 0 0;
    list-style: none;
}

.mbr-tier__row {
    font-size: var(--mbr-body-sm-size);
    line-height: 22px;
    color: var(--mbr-color-ink-secondary);
}

.mbr-tier__row--inline {
    display: flex;
    align-items: center;
    gap: 8px;
}

.mbr-tier__done {
    padding: 3px 10px;
    border-radius: var(--mbr-radius-md);
    background: var(--mbr-color-positive);
    font-size: 11px;
    font-weight: 700;
    line-height: 16px;
    color: var(--mbr-color-on-primary);
}

.mbr-tier__benefits {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin: 0;
    padding: 16px 0 0;
    list-style: none;
}

.mbr-tier__benefit {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.mbr-tier__tick {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-top: 2px;
    border-radius: var(--mbr-radius-full);
    background: var(--mbr-color-peach);
    color: var(--mbr-color-primary);
}

.mbr-tier__tick svg {
    width: 14px;
    height: 14px;
}

.mbr-tier__benefit-text {
    min-width: 0;
}

.mbr-tier__benefit-title {
    display: block;
    font-size: var(--mbr-body-sm-size);
    font-weight: 700;
    line-height: 18px;
    color: var(--mbr-color-ink-headline);
}

.mbr-tier__benefit-line {
    display: block;
    font-size: 12px;
    line-height: 18px;
    color: rgba(var(--mbr-color-ink-secondary-rgb), 0.8);
}

/* ---- empty state ---- */
.mbr-tier__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin: 48px 20px 0;
    padding: 32px 20px;
    background: var(--mbr-color-card);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.4);
    border-radius: var(--mbr-radius-lg);
    box-shadow: var(--mbr-shadow-card);
}

.mbr-tier__empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: var(--mbr-radius-full);
    background: var(--mbr-color-tint);
    color: var(--mbr-color-primary);
}

.mbr-tier__empty-icon svg {
    width: 26px;
    height: 26px;
}

.mbr-tier__empty-title {
    margin: 0;
    font-family: var(--mbr-font-headline);
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
    color: var(--mbr-color-ink-headline);
}
</style>
